import React, { useState } from "react";
import Papa from "papaparse";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Snackbar,
  Grid,
  Stack,
  Tooltip,
  Chip
} from "@mui/material";
import Header from "../../components/Header";

const MAX_NAME_LENGTH = 50;
const MIN_NAME_LENGTH = 2; // 🔹 新規追加：最小2文字
// 名前に使えない文字の正規表現
const FORBIDDEN_REGEX = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

const ClientCsvUpload = () => {
  const [csvData, setCsvData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // 🔹 具体的なエラーメッセージ生成
  const getNameErrorMessage = (name) => {
    if (!name) {
      return "名前が空です。";
    }
    if (name.length < MIN_NAME_LENGTH) {
      return "名前は2文字以上で入力してください。";
    }
    if (name.length > MAX_NAME_LENGTH) {
      return "名前が50文字を超えています。";
    }
    if (FORBIDDEN_REGEX.test(name)) {
      // 🚨 半角括弧の検出
      if (name.includes("(") || name.includes(")")) {
        return "名前に半角括弧が含まれています。「（株）」などは全角で入力してください。";
      }
      return "名前に使用できない文字が含まれています。";
    }
    return "";
  };

  // ファイル選択 → 文字化け検知 → パース
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 初期化
    setErrors([]);
    setCsvData([]);
    setSelectedFile(file);

    // 拡張子チェック
    if (!file.name.endsWith(".csv")) {
      setErrors(["CSVファイル（.csv）のみアップロードできます。"]);
      return;
    }

    // サイズチェック
    if (file.size > 1_000_000) {
      setErrors(["ファイルサイズが1MBを超えています。"]);
      return;
    }

    // UTF-8としてテキスト読み込み
    const reader = new FileReader();
    reader.onload = () => {
      const textContent = reader.result;

      // 文字化け検知
      if (textContent.includes("�")) {
        setErrors([
          "アップロードされたCSVが文字化けしている可能性があります。Excelなどで保存したCSVはShift_JIS形式になる場合があります。UTF-8形式で保存し直してください。"
        ]);
        return;
      }

      // 問題なさそうならパース
      parseCsvWithPapa(file);
    };

    reader.readAsText(file, "utf-8");
  };

  // CSVのパースと検証
  const parseCsvWithPapa = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const newErrors = [];
        const requiredFields = ["名前"]; // 住所は任意
        const fields = results.meta.fields || [];

        // カラムチェック
        for (const field of requiredFields) {
          if (!fields.includes(field)) {
            newErrors.push(`「${field}」の列が見つかりません。`);
          }
        }

        if (newErrors.length > 0) {
          setErrors(newErrors);
          setCsvData([]);
          return;
        }

        // 🔹 行単位チェック（改善版）
        const parsed = results.data.map((row, index) => {
          const lineNum = index + 2; // CSV上の2行目からデータなので
          const name = row["名前"]?.trim() || "";
          const address = row["住所"]?.trim() || "";

          const lineErrors = [];

          // 🔹 名前チェック（改善版）
          const nameError = getNameErrorMessage(name);
          if (nameError) {
            lineErrors.push(nameError);
          }

          // 住所チェック
          if (address && address.length > 100) {
            lineErrors.push("住所が100文字を超えています。");
          }

          if (lineErrors.length > 0) {
            newErrors.push(`${lineNum}行目：${lineErrors.join(" / ")}`);
          }

          return {
            name,
            address,
          };
        });

        setErrors(newErrors);
        setCsvData(newErrors.length === 0 ? parsed : []);
      },
      error: (err) => {
        setErrors([`CSV解析に失敗しました: ${err.message}`]);
        setCsvData([]);
      },
    });
  };

  // アップロード処理
  const handleUpload = async () => {
    if (!csvData.length) return;
    setUploading(true);
    try {
      const res = await fetch("/api/clients/clients/import", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
        body: JSON.stringify(csvData),
      });

      if (res.ok) {
        setSuccessMessage(`${csvData.length}件の顧客情報を登録しました！`);
        setSnackbarOpen(true);
        setCsvData([]);
        setErrors([]);
        setSelectedFile(null);
      } else {
        const text = await res.text();
        setErrors([text]);
      }
    } catch (err) {
      setErrors(["サーバーエラーが発生しました。"]);
    } finally {
      setUploading(false);
    }
  };

  // テンプレートCSVダウンロード
  const handleDownloadTemplate = () => {
    const csv = "名前,住所\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "顧客登録テンプレート.csv");
    link.click();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Header />

      {/* 説明ダイアログ */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoOutlinedIcon />
            <Typography variant="h6">CSVアップロードの説明</Typography>
          </Box>
          <IconButton onClick={() => setInfoOpen(false)} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* 基本的な手順 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              基本的な手順
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack spacing={2}>
                <Box display="flex" gap={2}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">1.</Typography>
                  <Typography variant="body2">テンプレートをダウンロード</Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">2.</Typography>
                  <Typography variant="body2">顧客情報を入力</Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">3.</Typography>
                  <Typography variant="body2">ファイルを選択</Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">4.</Typography>
                  <Typography variant="body2">アップロード</Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* CSVファイルの書式 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              CSVファイルの書式
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack spacing={2}>
                <Typography variant="body2">
                  CSVファイルは<strong>コンマ区切り</strong>のテキストファイルです。1行目にヘッダー（列名）を記載し、2行目以降に顧客データを入力してください。
                </Typography>
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  p: 1.5, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}>
                  <Typography component="pre" sx={{ m: 0, fontFamily: 'inherit' }}>
                    名前,住所
                    <br />
                    株式会社A,東京都千代田区...
                    <br />
                    株式会社B,大阪府大阪市...
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* CSVファイルの例 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              CSVファイル例
            </Typography>
            <Box 
              sx={{ 
                p: 1, 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 1,
                textAlign: 'center'
              }}
            >
              <img 
                src="/csv_sample.png" 
                alt="CSVファイル例" 
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                例：顧客名と住所をコンマで区切って入力したCSVファイル
              </Typography>
            </Box>
          </Box>

          {/* 🔹 各列の意味（更新版） */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              入力項目
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>項目名</TableCell>
                    <TableCell>説明</TableCell>
                    <TableCell align="center">必須</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>名前</strong></TableCell>
                    <TableCell>顧客名（2文字以上50文字以内）</TableCell>
                    <TableCell align="center">
                      <Chip label="必須" size="small" color="primary" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>住所</strong></TableCell>
                    <TableCell>顧客の住所（100文字以内）</TableCell>
                    <TableCell align="center">
                      <Chip label="任意" size="small" color="default" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* 🔹 注意事項（更新版） */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              注意事項
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack spacing={1}>
                <Typography variant="body2">• CSVは <strong>UTF-8</strong> 形式で保存してください</Typography>
                <Typography variant="body2">• エクセルで作成した場合は「CSV UTF-8（コンマ区切り）」形式で保存してください</Typography>
                <Typography variant="body2">• <strong>顧客名は2文字以上</strong>で入力してください</Typography>
                <Typography variant="body2">• 「（株）」「（有）」などは<strong>全角</strong>で入力してください</Typography>
                <Typography variant="body2">• 製品情報（総容量、月消費量など）の一括登録機能は近日公開予定です</Typography>
              </Stack>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* メインコンテンツ */}
      <Box sx={{ bgcolor: '#F5F7FA', minHeight: '100vh', pt: 4, pb: 8 }}>
        <Box maxWidth="800px" mx="auto" px={3}>
          {/* ヘッダーセクション */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              background: 'linear-gradient(to right, #ff9800, #ff7b00)',
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CloudUploadIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                  顧客情報 CSV一括登録
                </Typography>
                <Typography variant="body2">
                  CSVファイルを使って複数の顧客を一度に登録できます
                </Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* メインカード */}
          <Card sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              py: 2, 
              px: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle1" fontWeight="medium">
                CSVファイルのアップロード
              </Typography>
              <Tooltip title="アップロード方法の説明">
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={() => setInfoOpen(true)}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              {/* 🔹 入力ヒントメッセージ（更新版） */}
              <Alert 
                severity="info" 
                variant="outlined"
                icon={<HelpOutlineIcon />}
                sx={{ 
                  mb: 3, 
                  borderRadius: 1,
                  borderColor: 'info.light',
                }}
              >
                <strong>重要：</strong>顧客名は2文字以上で入力してください。「（株）」「（有）」などは全角で入力してください。現在、この機能では顧客の基本情報のみを登録できます。
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                    1. テンプレートをダウンロード
                  </Typography>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                    sx={{ mt: 1, py: 1.5 }}
                  >
                    テンプレートをダウンロード
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                    2. CSVファイルを選択
                  </Typography>
                  <Box sx={{ 
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    bgcolor: selectedFile ? 'primary.50' : 'grey.50',
                    position: 'relative',
                    height: '52px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      style={{
                        opacity: 0,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        zIndex: 10
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileUploadIcon color={selectedFile ? "primary" : "action"} />
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {selectedFile ? selectedFile.name : 'クリックしてファイルを選択'}
                      </Typography>
                      {selectedFile && (
                        <Chip 
                          label={`${(selectedFile.size / 1024).toFixed(1)} KB`} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Box mt={4} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleUpload}
                  disabled={uploading || !csvData.length}
                  startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                  sx={{ px: 4, py: 1 }}
                >
                  {uploading ? "アップロード中..." : "アップロード"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          
          {/* エラー表示 */}
          {errors.length > 0 && (
            <Card 
              sx={{ 
                borderRadius: 2, 
                mb: 3, 
                borderLeft: '4px solid',
                borderColor: 'error.main',
                boxShadow: 'none',
                bgcolor: 'error.50'
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <WarningAmberIcon color="error" sx={{ mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight="medium" color="error.main" gutterBottom>
                      エラーが発生しました
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: '150px',
                        overflowY: 'auto',
                      }}
                    >
                      <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                        {errors.map((err, idx) => (
                          <li key={idx}>
                            <Typography variant="body2">{err}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
          
          {/* CSVプレビュー */}
          {csvData.length > 0 && (
            <Card sx={{ borderRadius: 2 }}>
              <Box sx={{ 
                py: 2, 
                px: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  プレビュー
                </Typography>
                <Chip
                  label={`${csvData.length}件`}
                  color="primary"
                  size="small"
                />
              </Box>
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="60px">#</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">名前</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <HomeIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">住所</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.address || "未入力"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Box>
      </Box>
      
      {/* 成功通知 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled"
          icon={<CheckCircleIcon />}
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClientCsvUpload;